// g2-feature-dispatch-check.ts
//
// The G2 step of feature-dispatch-gate-guard.ts, split out to keep the guard
// under the 200-line budget. H4 A1 (design bản 2, 2026-09-29):
//   - plan not ready (a bootstrap ticket still open) ⇒ every feature ticket
//     waits with `g2_plan_not_ready` — including one that already sat in the
//     repo at base_sha (measured: it ran 3 times while the plan was blocked);
//   - plan ready ⇒ G2 must be `approved` or `waived`; `not_requested` is no
//     longer a pass (fail closed — the enforcement step requests it this tick);
//   - readiness unknown (caller did not evaluate G2) ⇒ the pre-H4 rule:
//     only `pending`/`invalidated` block.
import type { FeatureDispatchGateDecision } from "./feature-dispatch-gate-guard.js";
import type { G2PlanReadiness } from "./g2-plan-readiness.js";
import { approveHint } from "./gate-approve-command-hint.js";
import type { TicketKind } from "./schemas/ticket-schema.js";

const G2_CLEARED = new Set(["approved", "waived"]);

export function checkG2BeforeFeatureDispatch(input: {
  ticketKind: TicketKind;
  ticketId?: string;
  g2Status: string;
  g2PlanReadiness?: G2PlanReadiness;
}): FeatureDispatchGateDecision | undefined {
  const readiness = input.g2PlanReadiness;
  const exempt =
    input.ticketId !== undefined && (readiness?.exemptTicketIds ?? []).includes(input.ticketId);
  if (readiness !== undefined && !readiness.ready && exempt) return undefined;
  if (readiness !== undefined && !readiness.ready) {
    return {
      blocked: true,
      code: "g2_plan_not_ready",
      reason: `ticket kind "${input.ticketKind}" là feature — ${readiness.reason ?? "plan chưa sẵn sàng."}`,
    };
  }
  const g2 = input.g2Status;
  const blocks =
    readiness === undefined ? g2 === "pending" || g2 === "invalidated" : !G2_CLEARED.has(g2);
  if (!blocks) return undefined;
  return {
    blocked: true,
    code: "gate_not_approved",
    reason:
      `ticket kind "${input.ticketKind}" là feature nhưng G2 (plan) đang ${g2} — G2 gắn với nội ` +
      "dung plan hiện tại (xem request id trong `avatar autopilot status`). " +
      `${approveHint("g2")}.`,
  };
}
